import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBuilders, useJobs, useStages } from '../lib/queries';
import { useFlatworkEvents } from '../lib/flatworkSchedule';
import { useAuth } from '../auth/AuthProvider';
import { Button, EmptyState, Input, Label, Select } from '../components/ui';
import { JobRow, rowGridTemplate } from './JobRow';
import { ScheduleStageDialog } from './ScheduleStageDialog';
import { EditJobDialog } from './EditJobDialog';
import { FlatworkScheduleDialog } from './FlatworkScheduleDialog';
import { BulkFlatworkDialog } from './BulkFlatworkDialog';
import { FLATWORK_TYPE_LABELS, STATUS_LABELS } from '../lib/database.types';
import type { FlatworkEvent, JobWithContext, Stage, StageStatus } from '../lib/database.types';
import { isJobCompleted, isWaitingOnFlatwork, overallStatus, visibleStages } from '../lib/boardStatus';
import { cn } from '../lib/utils';
import { ColumnMenu, type SortDir } from '../components/ColumnMenu';
import { SortPanel, type SortLevel } from '../components/SortPanel';
import { ColumnsPanel } from '../components/ColumnsPanel';
import { useSetUserPreferences, useUserPreferences } from '../lib/userPreferences';

type DialogState =
    | { kind: 'none' }
    | { kind: 'schedule'; jobId: string; jobStageId: string; stageName: string; jobLabel: string; currentDate: string | null; currentCrewId: string | null }
    | { kind: 'edit-job'; job: JobWithContext | null }
    | { kind: 'flatwork'; jobId: string; jobLabel: string; current: FlatworkEvent[] }
    | { kind: 'bulk-flatwork' };

type StatusFilter = 'active' | 'completed';

// Statuses the office filters by, in board-reading order.
const COLOR_FILTERS: StageStatus[] = ['scheduled', 'in_progress', 'on_hold', 'complete', 'pending'];

const PILL_WAITING = 'Slab Done · FW Pending';
const PILL_KEPT = 'Kept On Board';
const CREW_NONE = 'No crew';

/** Pull the "FW Ready: YYYY-MM-DD" date Andrea stores in the notes field. */
function extractFwReady(notes: string | null): string | null {
    if (!notes) return null;
    const m = notes.match(/FW Ready:\s*(\d{4}-\d{2}-\d{2})/i);
    return m ? m[1] : null;
}

// -----------------------------------------------------------------------------
// Column addressing
// -----------------------------------------------------------------------------
// Column ids: 'job' | 'notes' | 'stage:<stageId>' | 'fwReady' | 'fwSched' | 'crew' | 'status'.
//
// Every column answers three questions, and they are deliberately different:
//   columnText  - free text a "contains" box searches
//   columnFacet - the one discrete value the checkbox list groups by
//   columnSort  - the value the column sorts on
//
// Keeping them apart is what makes the filter behave like Excel rather than a
// search box. A stage column sorts by date but filters by status, and mashing
// both into one string is why typing "complete" into the old filter row also
// matched a crew named Complete.

function columnText(job: JobWithContext, colId: string, flatwork: FlatworkEvent[]): string {
    if (colId === 'job') {
        return [job.builder.prefix, job.builder.name, job.community.name, job.lot_number, job.address ?? '', job.contact_name ?? '']
            .join(' ');
    }
    if (colId === 'notes') return job.notes ?? '';
    if (colId === 'fwReady') return job.fw_ready_date ?? extractFwReady(job.notes) ?? '';
    if (colId === 'crew') return job.crew?.name ?? '';
    if (colId === 'fwSched') {
        if (!flatwork.length) return '';
        const date = flatwork[0].actual_completion_date ?? flatwork[0].scheduled_date ?? '';
        const types = flatwork.map((f) => FLATWORK_TYPE_LABELS[f.event_type]).join(' ');
        return `${date} ${types}`;
    }
    if (colId === 'status') return columnFacet(job, colId, flatwork);
    if (colId.startsWith('stage:')) {
        const js = job.stages.find((s) => s.stage_id === colId.slice('stage:'.length));
        if (!js) return '';
        const date = js.actual_completion_date ?? js.scheduled_date ?? '';
        return `${date} ${js.crew?.name ?? ''}`;
    }
    return '';
}

function columnFacet(job: JobWithContext, colId: string, flatwork: FlatworkEvent[]): string {
    if (colId === 'job') return job.community.name;
    if (colId === 'notes') return job.notes?.trim() ? 'Has notes' : 'Empty';
    // Unassigned is a real answer to "which crew", not a blank, so it gets a
    // label. Andrea filters this column to build each crew's daily list and
    // needs to see what has fallen through.
    if (colId === 'crew') return job.crew?.name ?? CREW_NONE;
    if (colId === 'fwReady') {
        // Andrea's "filter by colour" for this column: orange means the builder
        // gave the go-ahead, which is a different fact from a date being set.
        if (job.fw_ready_confirmed) return 'Ready (orange)';
        return job.fw_ready_date ?? extractFwReady(job.notes) ? 'Date set' : 'Not set';
    }
    if (colId === 'fwSched') {
        if (!flatwork.length) return 'Not scheduled';
        if (flatwork.every((f) => f.status === 'complete')) return 'Complete';
        return flatwork.map((f) => FLATWORK_TYPE_LABELS[f.event_type]).join(', ');
    }
    if (colId === 'status') {
        if (isWaitingOnFlatwork(job, flatwork)) return PILL_WAITING;
        if (job.force_active) return PILL_KEPT;
        return STATUS_LABELS[overallStatus(job)];
    }
    if (colId.startsWith('stage:')) {
        const js = job.stages.find((s) => s.stage_id === colId.slice('stage:'.length));
        return js ? STATUS_LABELS[js.status] : 'Not set';
    }
    return '';
}

function columnSort(job: JobWithContext, colId: string, flatwork: FlatworkEvent[]): string {
    if (colId === 'job') return `${job.community.name} ${job.lot_number}`;
    if (colId === 'notes') return job.notes ?? '';
    if (colId === 'fwReady') return job.fw_ready_date ?? extractFwReady(job.notes) ?? '';
    if (colId === 'crew') return job.crew?.name ?? '';
    if (colId === 'fwSched') {
        if (!flatwork.length) return '';
        return flatwork[0].actual_completion_date ?? flatwork[0].scheduled_date ?? '';
    }
    if (colId === 'status') return columnFacet(job, colId, flatwork);
    if (colId.startsWith('stage:')) {
        const js = job.stages.find((s) => s.stage_id === colId.slice('stage:'.length));
        if (!js) return '';
        // Dates are ISO, so a plain string compare is already chronological.
        return js.actual_completion_date ?? js.scheduled_date ?? '';
    }
    return '';
}

/**
 * Blanks always sort last, in both directions, the way a spreadsheet does. A
 * descending sort that leads with forty empty cells is not a useful view of the
 * board. numeric:true is what makes lot 9 come before lot 210.
 */
function compareValues(a: string, b: string, dir: SortDir): number {
    const aEmpty = a.trim() === '';
    const bEmpty = b.trim() === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    const cmp = a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    return dir === 'asc' ? cmp : -cmp;
}

/**
 * Walk the sort levels in order and return the first one that separates the two
 * rows. This is Excel's model and it is what makes Andrea's end-of-day view
 * possible: orange to the top, then yellow, then green to the bottom, then
 * dates oldest first inside whatever is left.
 *
 * A status level ranks a row 0, 1 or 2. The named status is 0 when it goes on
 * top and 2 when it goes on the bottom, everything else is 1, so one level
 * splits the list into at most three blocks and later levels order within them.
 */
function compareByLevels(
    a: JobWithContext,
    b: JobWithContext,
    levels: SortLevel[],
    fwFor: (job: JobWithContext) => FlatworkEvent[],
): number {
    for (const lv of levels) {
        if (lv.kind === 'status') {
            const rank = (j: JobWithContext) => {
                const v = columnFacet(j, lv.colId, fwFor(j));
                if (v !== lv.value) return 1;
                return lv.position === 'top' ? 0 : 2;
            };
            const d = rank(a) - rank(b);
            if (d !== 0) return d;
        } else {
            const d = compareValues(
                columnSort(a, lv.colId, fwFor(a)),
                columnSort(b, lv.colId, fwFor(b)),
                lv.dir,
            );
            if (d !== 0) return d;
        }
    }
    return 0;
}

function csvCell(value: string | null | undefined): string {
    const s = (value ?? '').replace(/\r?\n/g, ' ').replace(/"/g, '""');
    return `"${s}"`;
}

function exportJobsCsv(
    jobs: JobWithContext[],
    view: StatusFilter,
    flatworkByJob: Map<string, FlatworkEvent[]>,
    pipelineStages: Stage[],
) {
    // Crew sits early on purpose. The whole point of this export is that Andrea
    // filters to one crew and sends them the slice, so it should be readable at
    // a glance rather than buried past the dates.
    //
    // The task columns are built from pipelineStages, the same list the board
    // renders, rather than a hand-written header. The hand-written one carried
    // no task columns at all: Pin, Box, Plumbing Inspection, Prep and Slab
    // Inspection were absent entirely and Pour survived only as a collapsed
    // "Pour Date". Andrea, 1 Sep: "all columns from pin-pour do not export and
    // those are the main columns I would need." Driving both off one list is
    // what stops the export and the board drifting apart again.
    //
    // Two columns per task, date and status. Colour does not survive a
    // spreadsheet export, and the status IS the colour, so it needs a column of
    // its own. Same call already made for Concrete and Extra time on the
    // service board export.
    //
    // Dates go out raw as yyyy-mm-dd rather than the board's "Aug 21", because
    // Excel reads that as a real date and she sorts and filters on it.
    const header = [
        'Builder', 'Community', 'Lot', 'Crew', 'Address', 'Contact', 'View',
        ...pipelineStages.flatMap((s) => [s.name, `${s.name} Status`]),
        'FW Ready', 'FW Ready confirmed', 'FW Scheduled', 'Status', 'Notes',
    ];
    const rows = jobs.map((j) => {
        const byStageId = new Map(j.stages.map((js) => [js.stage_id, js]));
        const fw = flatworkByJob.get(j.id) ?? [];
        const fwSched =
            fw.length && fw[0].scheduled_date
                ? `${fw[0].scheduled_date} (${fw.map((f) => FLATWORK_TYPE_LABELS[f.event_type]).join(', ')})`
                : '';
        const stageCells = pipelineStages.flatMap((s) => {
            const js = byStageId.get(s.id);
            if (!js) return ['', ''];
            // Completed tasks report the day they were actually done. That is
            // what the board cell shows, so the export agrees with the screen.
            const d = js.status === 'complete'
                ? (js.actual_completion_date ?? js.scheduled_date ?? '')
                : (js.scheduled_date ?? '');
            return [d, STATUS_LABELS[js.status] ?? js.status];
        });
        return [
            j.builder.name,
            j.community.name,
            j.lot_number,
            j.crew?.name ?? '',
            j.address,
            j.contact_name,
            view,
            ...stageCells,
            j.fw_ready_date ?? extractFwReady(j.notes),
            j.fw_ready_confirmed ? 'yes' : '',
            fwSched,
            columnFacet(j, 'status', fw),
            j.notes,
        ].map(csvCell).join(',');
    });
    const csv = [header.map(csvCell).join(','), ...rows].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ppc-board-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export function BoardPage() {
    const { role } = useAuth();
    const { data: stages = [] } = useStages();
    const { data: builders = [] } = useBuilders();
    const { data: jobs, isLoading, error } = useJobs();
    const { data: flatworkEvents = [] } = useFlatworkEvents();

    const [builderFilter, setBuilderFilter] = useState<string>('all');
    const [statusColor, setStatusColor] = useState<'all' | StageStatus>('all');
    const [search, setSearch] = useState('');
    const [onlyFlatwork, setOnlyFlatwork] = useState(false);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
    const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

    const isAdmin = role === 'admin';

    // Per-column state, keyed by column id. Two independent filters per column,
    // ANDed: a contains box for open text, and a checked set of discrete values.
    const [sortLevels, setSortLevels] = useState<SortLevel[]>([]);
    const [sortPanelOpen, setSortPanelOpen] = useState(false);
    // Deliberately not persisted. Andrea, 2 Sep: she likes the full rows for
    // daily work and wants the tight ones only to grab and send, so this is a
    // toggle she flips both ways in a minute, not a setting.
    const [compact, setCompact] = useState(false);
    const [columnsPanel, setColumnsPanel] = useState<{ x: number; y: number } | null>(null);

    // Hidden rather than visible, so a column added later shows up for
    // everyone instead of being invisible to anyone who ever saved a
    // preference. Job is never offered, it carries the lot and the click
    // target that opens the job.
    const { data: prefs } = useUserPreferences();
    const setPrefs = useSetUserPreferences();
    const hiddenColumns = prefs?.board?.hiddenColumns ?? [];
    const LOCKED_COLUMNS = ['job'];

    function setHiddenColumns(next: string[]) {
        setPrefs.mutate({ ...prefs, board: { ...prefs?.board, hiddenColumns: next } });
    }
    const [textFilters, setTextFilters] = useState<Record<string, string>>({});
    const [valueFilters, setValueFilters] = useState<Record<string, string[]>>({});
    const [menu, setMenu] = useState<{ colId: string; label: string; x: number; y: number } | null>(null);

    // A lot can have several flatwork rows (one per type). Group by job_id.
    const flatworkByJob = useMemo(() => {
        const m = new Map<string, FlatworkEvent[]>();
        for (const fe of flatworkEvents) {
            const arr = m.get(fe.job_id);
            if (arr) arr.push(fe);
            else m.set(fe.job_id, [fe]);
        }
        return m;
    }, [flatworkEvents]);

    // Split jobs into active vs completed once per render. isJobCompleted lives
    // in lib/boardStatus so this split and the row's own status badge can never
    // drift apart again.
    const { activeJobs, completedJobs } = useMemo(() => {
        const active: JobWithContext[] = [];
        const completed: JobWithContext[] = [];
        for (const j of jobs ?? []) {
            if (isJobCompleted(j, flatworkByJob.get(j.id) ?? [])) completed.push(j);
            else active.push(j);
        }
        return { activeJobs: active, completedJobs: completed };
    }, [jobs, flatworkByJob]);

    // Only the stages the board shows. Plumbing comes out because Andrea tracks
    // Plumbing Inspection only; Complete is a terminal marker, not field work.
    const pipelineStages = useMemo(() => visibleStages(stages as Stage[]), [stages]);

    const headerPad = compact ? 'px-3 py-0.5' : 'px-3 py-2.5';

    const activeTextFilters = useMemo(
        () => Object.entries(textFilters)
            .map(([col, val]) => [col, val.trim().toLowerCase()] as const)
            .filter(([, val]) => val.length > 0),
        [textFilters]
    );

    const activeValueFilters = useMemo(
        () => Object.entries(valueFilters)
            .map(([col, vals]) => [col, new Set(vals)] as const)
            .filter(([, vals]) => vals.size > 0),
        [valueFilters]
    );

    const filterCount = activeTextFilters.length + activeValueFilters.length;

    const filteredJobs = useMemo(() => {
        const source = statusFilter === 'active' ? activeJobs : completedJobs;
        // Every term has to appear somewhere, in any order. A single substring
        // match meant "Bailey Run 115" found nothing, because the haystack reads
        // "115 MY Bailey Run" and those words never sit in that order. Andrea
        // types community then lot, the way her sheet reads.
        const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);

        const rows = source.filter((j) => {
            if (builderFilter !== 'all' && j.builder_id !== builderFilter) return false;
            if (onlyFlatwork && !j.flatwork_active) return false;
            if (statusColor !== 'all' && !j.stages.some((s) => s.status === statusColor)) return false;

            const fw = flatworkByJob.get(j.id) ?? [];

            for (const [col, val] of activeTextFilters) {
                if (!columnText(j, col, fw).toLowerCase().includes(val)) return false;
            }
            for (const [col, vals] of activeValueFilters) {
                if (!vals.has(columnFacet(j, col, fw))) return false;
            }

            if (terms.length === 0) return true;
            const hay = [
                j.lot_number,
                j.community.name,
                j.builder.name,
                j.builder.prefix,
                j.address ?? '',
                j.contact_name ?? '',
                j.notes ?? '',
                j.crew?.name ?? '',
            ]
                .join(' ')
                .toLowerCase();
            return terms.every((t) => hay.includes(t));
        });

        if (sortLevels.length) {
            const fwFor = (j: JobWithContext) => flatworkByJob.get(j.id) ?? [];
            rows.sort((a, b) => compareByLevels(a, b, sortLevels, fwFor));
        }

        return rows;
    }, [
        activeJobs, completedJobs, statusFilter, builderFilter, statusColor, onlyFlatwork,
        search, activeTextFilters, activeValueFilters, sortLevels, flatworkByJob,
    ]);

    // Selection survives filter changes on purpose: narrow to one community,
    // tick those, narrow to the next, tick those, then act on the lot. It is
    // resolved against every job rather than the filtered rows so nothing
    // silently drops out of the count when the filter moves.
    const selectedJobs = useMemo(
        () => (jobs ?? []).filter((j) => selectedIds.has(j.id)),
        [jobs, selectedIds]
    );

    const allFilteredSelected =
        filteredJobs.length > 0 && filteredJobs.every((j) => selectedIds.has(j.id));
    const someFilteredSelected =
        !allFilteredSelected && filteredJobs.some((j) => selectedIds.has(j.id));

    function toggleSelectAllFiltered() {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                for (const j of filteredJobs) next.delete(j.id);
            } else {
                for (const j of filteredJobs) next.add(j.id);
            }
            return next;
        });
    }

    function toggleOne(jobId: string) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(jobId)) next.delete(jobId);
            else next.add(jobId);
            return next;
        });
    }

    // Facet values offered in the open menu, drawn from the current tab so the
    // list only ever shows values that actually exist on the board.
    const facetSource = statusFilter === 'active' ? activeJobs : completedJobs;

    const facetsFor = useCallback(
        (colId: string) => {
            const seen = new Set<string>();
            for (const j of facetSource) {
                const v = columnFacet(j, colId, flatworkByJob.get(j.id) ?? []);
                if (v) seen.add(v);
            }
            return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
        },
        [facetSource, flatworkByJob],
    );

    const menuFacets = useMemo(
        () => (menu ? facetsFor(menu.colId) : ([] as string[])),
        [menu, facetsFor],
    );

    function openMenu(e: React.MouseEvent<HTMLButtonElement>, colId: string, label: string) {
        const r = e.currentTarget.getBoundingClientRect();
        setMenu({
            colId,
            label,
            // Clamp so a right-hand column's menu stays on screen.
            x: Math.min(r.left, window.innerWidth - 296),
            y: r.bottom + 2,
        });
    }

    function clearAllFilters() {
        setTextFilters({});
        setValueFilters({});
        setSortLevels([]);
    }

    function openSchedule(jobId: string, jobStageId: string, stageName: string, jobLabel: string, currentDate: string | null, currentCrewId: string | null) {
        setDialog({ kind: 'schedule', jobId, jobStageId, stageName, jobLabel, currentDate, currentCrewId });
    }

    const headerCells: Array<{ id: string; label: string; className?: string; title?: string }> = [
        { id: 'job', label: 'Job' },
        { id: 'notes', label: 'Notes' },
        ...pipelineStages.map((s) => ({
            id: `stage:${s.id}`,
            label: s.name,
            className: s.ppc_controlled ? 'text-signal-600 dark:text-signal-400' : 'text-graphite-500',
            title: s.ppc_controlled ? 'PPC controlled' : 'Builder / inspection controlled',
        })),
        { id: 'fwReady', label: 'FW Ready' },
        { id: 'fwSched', label: 'FW Sched', className: 'text-signal-600 dark:text-signal-400' },
        { id: 'crew', label: 'Crew', className: 'text-signal-600 dark:text-signal-400', title: 'Crew for the whole job' },
        { id: 'status', label: 'Status' },
    ];

    // One list drives the header, the grid template and the row cells. A column
    // cannot be hidden in one place and left behind in another.
    const visibleHeaderCells = headerCells.filter((h) => !hiddenColumns.includes(h.id));
    const visibleColIds = visibleHeaderCells.map((h) => h.id);
    const columns = rowGridTemplate(visibleColIds, isAdmin);

    return (
        <div className="flex flex-col h-[calc(100vh-3.5rem)]">
            {/* Toolbar (static, always visible above the scrolling board) */}
            <div className="shrink-0 bg-graphite-50 dark:bg-graphite-950 border-b border-graphite-200 dark:border-graphite-800 px-4 md:px-6 py-3">
                <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-4">
                    <div className="flex-1 flex flex-wrap items-end gap-3">
                        {/* Active / Completed segmented toggle */}
                        <div className="flex flex-col">
                            <Label htmlFor="status-filter-active">View</Label>
                            <div
                                role="tablist"
                                aria-label="Job status filter"
                                className="inline-flex border border-graphite-300 dark:border-graphite-700 rounded overflow-hidden h-9"
                            >
                                <button
                                    id="status-filter-active"
                                    role="tab"
                                    type="button"
                                    aria-selected={statusFilter === 'active'}
                                    onClick={() => {
                                        setStatusFilter('active');
                                        setSelectedIds(new Set());
                                    }}
                                    className={cn(
                                        'px-3 font-mono text-xs uppercase tracking-widest transition-colors flex items-center gap-2',
                                        statusFilter === 'active'
                                            ? 'bg-signal-500 text-white'
                                            : 'bg-transparent text-graphite-600 dark:text-graphite-400 hover:bg-graphite-100 dark:hover:bg-graphite-900'
                                    )}
                                >
                                    Active
                                    <span className="tabular text-[10px] opacity-80">{activeJobs.length}</span>
                                </button>
                                <button
                                    id="status-filter-completed"
                                    role="tab"
                                    type="button"
                                    aria-selected={statusFilter === 'completed'}
                                    onClick={() => {
                                        setStatusFilter('completed');
                                        setSelectedIds(new Set());
                                    }}
                                    className={cn(
                                        'px-3 font-mono text-xs uppercase tracking-widest transition-colors flex items-center gap-2 border-l border-graphite-300 dark:border-graphite-700',
                                        statusFilter === 'completed'
                                            ? 'bg-done-500 text-white'
                                            : 'bg-transparent text-graphite-600 dark:text-graphite-400 hover:bg-graphite-100 dark:hover:bg-graphite-900'
                                    )}
                                >
                                    Completed
                                    <span className="tabular text-[10px] opacity-80">{completedJobs.length}</span>
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 min-w-[180px] max-w-xs">
                            <Label htmlFor="search">Search</Label>
                            <Input
                                id="search"
                                placeholder="Lot, community, address, contact, crew, notes..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="w-44">
                            <Label htmlFor="builder-filter">Builder</Label>
                            <Select
                                id="builder-filter"
                                value={builderFilter}
                                onChange={(e) => setBuilderFilter(e.target.value)}
                            >
                                <option value="all">All builders</option>
                                {builders.map((b) => (
                                    <option key={b.id} value={b.id}>
                                        {b.name}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div className="w-44">
                            {/* Distinct from the Status column filter: this matches a job
                                if ANY of its stages sits in the chosen state. */}
                            <Label htmlFor="status-color-filter">Any stage is</Label>
                            <Select
                                id="status-color-filter"
                                value={statusColor}
                                onChange={(e) => setStatusColor(e.target.value as 'all' | StageStatus)}
                            >
                                <option value="all">Any status</option>
                                {COLOR_FILTERS.map((s) => (
                                    <option key={s} value={s}>
                                        {STATUS_LABELS[s]}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <label className="flex items-center gap-2 h-9 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={onlyFlatwork}
                                onChange={(e) => setOnlyFlatwork(e.target.checked)}
                                className="accent-signal-500"
                            />
                            <span className="font-mono text-xs uppercase tracking-widest text-graphite-600 dark:text-graphite-400">
                                Flatwork only
                            </span>
                        </label>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="font-mono text-xs text-graphite-500 mr-2 tabular">
                            {filteredJobs.length} / {statusFilter === 'active' ? activeJobs.length : completedJobs.length} jobs
                        </div>
                        <Button
                            variant={sortLevels.length ? 'primary' : 'ghost'}
                            size="md"
                            onClick={() => setSortPanelOpen(true)}
                            title="Stack several sort levels, the way Excel does"
                        >
                            Sort{sortLevels.length ? ` (${sortLevels.length})` : ''}
                        </Button>
                        <Button
                            variant={hiddenColumns.length ? 'primary' : 'ghost'}
                            size="md"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setColumnsPanel({
                                    x: Math.min(r.left, window.innerWidth - 272),
                                    y: r.bottom + 2,
                                });
                            }}
                            title="Show or hide columns, saved to your account"
                        >
                            Columns{hiddenColumns.length ? ` (${hiddenColumns.length})` : ''}
                        </Button>
                        <Button
                            variant={compact ? 'primary' : 'ghost'}
                            size="md"
                            onClick={() => setCompact((v) => !v)}
                            title="Squeeze the rows to one line so a filtered list fits in one screen grab"
                        >
                            {compact ? 'Full rows' : 'Compact'}
                        </Button>
                        {filterCount > 0 || sortLevels.length ? (
                            <Button variant="ghost" size="md" onClick={clearAllFilters}>
                                Clear {filterCount > 0 ? `(${filterCount})` : 'sort'}
                            </Button>
                        ) : null}
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={() => exportJobsCsv(filteredJobs, statusFilter, flatworkByJob, pipelineStages)}
                            disabled={filteredJobs.length === 0}
                        >
                            Export
                        </Button>
                        {isAdmin ? (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() => setDialog({ kind: 'edit-job', job: null })}
                            >
                                + New job
                            </Button>
                        ) : null}
                    </div>
                </div>
                <p className="mt-2 font-mono text-[10px] text-graphite-400 dark:text-graphite-600">
                    Click any column heading to sort it or filter it by value.
                    {isAdmin ? ' Tick rows to act on several lots at once.' : ''}
                </p>
            </div>

            {/* Bulk action bar. Only appears with a selection, so it costs nothing
                when she is working one lot at a time. */}
            {isAdmin && selectedJobs.length > 0 ? (
                <div className="shrink-0 bg-signal-500/10 border-b border-signal-500/30 px-4 md:px-6 py-2 flex flex-wrap items-center gap-3">
                    <span className="font-mono text-xs uppercase tracking-widest text-signal-700 dark:text-signal-300 tabular">
                        {selectedJobs.length} selected
                    </span>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setDialog({ kind: 'bulk-flatwork' })}
                    >
                        Flatwork on these
                    </Button>
                    <div className="flex-1" />
                    <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                        Clear selection
                    </Button>
                </div>
            ) : null}

            {/* Board (the only scroller; horizontal scrollbar sits at the viewport bottom) */}
            <div className="flex-1 min-h-0 overflow-auto">
                {error ? (
                    <div className="m-6 border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 rounded font-mono text-sm">
                        Error loading board: {(error as Error).message}
                    </div>
                ) : null}

                {isLoading ? (
                    <div className="px-6 py-10 font-mono text-xs text-graphite-500 uppercase tracking-widest">
                        Loading jobs...
                    </div>
                ) : filteredJobs.length === 0 ? (
                    <div className="p-6">
                        <EmptyState
                            title={
                                jobs?.length === 0
                                    ? 'No jobs yet'
                                    : statusFilter === 'completed'
                                        ? 'No completed jobs match your filters'
                                        : 'No active jobs match your filters'
                            }
                            description={
                                jobs?.length === 0
                                    ? 'Import from Andrea\'s Schedule.xlsx or create a job to get started.'
                                    : statusFilter === 'completed'
                                        ? 'Jobs land here once the slab is done and flatwork is marked complete.'
                                        : 'Try clearing the search or the column filters.'
                            }
                        />
                    </div>
                ) : (
                    <div className="min-w-max">
                        {/* Grid header. Each heading opens its own sort and filter menu. */}
                        <div
                            className={cn(
                                'sticky top-0 z-10 bg-graphite-50 dark:bg-graphite-950 border-b border-graphite-200 dark:border-graphite-800',
                                'grid items-stretch'
                            )}
                            style={{ gridTemplateColumns: columns }}
                        >
                            {isAdmin ? (
                                <label
                                    className={cn(headerPad, 'border-r border-graphite-200 dark:border-graphite-800 flex items-center justify-center cursor-pointer select-none hover:bg-graphite-200/70 dark:hover:bg-graphite-800/70')}
                                    title={allFilteredSelected ? 'Unselect these rows' : 'Select every row shown'}
                                >
                                    <input
                                        type="checkbox"
                                        checked={allFilteredSelected}
                                        ref={(el) => {
                                            if (el) el.indeterminate = someFilteredSelected;
                                        }}
                                        onChange={toggleSelectAllFiltered}
                                        className="accent-signal-500"
                                        aria-label="Select every row shown"
                                    />
                                </label>
                            ) : null}
                            {visibleHeaderCells.map((h, i) => {
                                // A column can appear in several levels. The
                                // arrow reflects its first value level, and the
                                // number is its place in the stack, so the header
                                // row alone says what order the sort runs in.
                                const levelIndex = sortLevels.findIndex((l) => l.colId === h.id);
                                const level = levelIndex >= 0 ? sortLevels[levelIndex] : null;
                                const sorted = level && level.kind === 'value' ? level.dir : null;
                                const filtered =
                                    (textFilters[h.id]?.trim().length ?? 0) > 0 ||
                                    (valueFilters[h.id]?.length ?? 0) > 0;
                                return (
                                    <button
                                        key={h.id}
                                        type="button"
                                        onClick={(e) => openMenu(e, h.id, h.label)}
                                        title={h.title ?? `Sort or filter ${h.label}`}
                                        className={cn(
                                            headerPad,
                                            'font-mono text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5 text-left transition-colors',
                                            'hover:bg-graphite-200/70 dark:hover:bg-graphite-800/70 cursor-pointer',
                                            i < headerCells.length - 1 && 'border-r border-graphite-200 dark:border-graphite-800',
                                            h.className ?? 'text-graphite-500',
                                            (level || filtered) && 'bg-signal-500/[0.08]'
                                        )}
                                    >
                                        <span className="truncate">{h.label}</span>
                                        {level ? (
                                            <span className="text-signal-600 dark:text-signal-400">
                                                {sorted ? (sorted === 'asc' ? '↑' : '↓') : '⇅'}
                                                {sortLevels.length > 1 ? (
                                                    <span className="tabular text-[9px]">
                                                        {levelIndex + 1}
                                                    </span>
                                                ) : null}
                                            </span>
                                        ) : null}
                                        {filtered ? (
                                            <span className="text-signal-600 dark:text-signal-400">●</span>
                                        ) : null}
                                    </button>
                                );
                            })}
                        </div>

                        {filteredJobs.map((job) => (
                            <JobRow
                                key={job.id}
                                job={job}
                                stages={pipelineStages}
                                compact={compact}
                                visibleColIds={visibleColIds}
                                canEdit={isAdmin}
                                flatwork={flatworkByJob.get(job.id) ?? []}
                                showSelect={isAdmin}
                                selected={selectedIds.has(job.id)}
                                onToggleSelect={() => toggleOne(job.id)}
                                onScheduleStage={openSchedule}
                                onScheduleFlatwork={() =>
                                    setDialog({
                                        kind: 'flatwork',
                                        jobId: job.id,
                                        jobLabel: `${job.community.name} · Lot ${job.lot_number}`,
                                        current: flatworkByJob.get(job.id) ?? [],
                                    })
                                }
                                onEditJob={() => setDialog({ kind: 'edit-job', job })}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Column menu, rendered in a portal so the board's overflow cannot clip it. */}
            {menu
                ? createPortal(
                    <ColumnMenu
                        label={menu.label}
                        x={menu.x}
                        y={menu.y}
                        facets={menuFacets}
                        sortDir={(() => {
                            const l = sortLevels.find(
                                (x) => x.colId === menu.colId && x.kind === 'value',
                            );
                            return l && l.kind === 'value' ? l.dir : null;
                        })()}
                        text={textFilters[menu.colId] ?? ''}
                        selected={valueFilters[menu.colId] ?? []}
                        // A quick sort from a heading replaces the whole
                        // stack, which is what Excel's A to Z button does. The
                        // panel is where a stack is built and kept.
                        onSort={(dir) =>
                            setSortLevels(dir ? [{ kind: 'value', colId: menu.colId, dir }] : [])
                        }
                        onText={(v) =>
                            setTextFilters((prev) => {
                                const next = { ...prev };
                                if (v) next[menu.colId] = v;
                                else delete next[menu.colId];
                                return next;
                            })
                        }
                        onSelected={(vals) =>
                            setValueFilters((prev) => {
                                const next = { ...prev };
                                if (vals.length) next[menu.colId] = vals;
                                else delete next[menu.colId];
                                return next;
                            })
                        }
                        onClose={() => setMenu(null)}
                    />,
                    document.body
                )
                : null}

            {/* Sort panel, also portalled so the board's overflow cannot clip it. */}
            {sortPanelOpen
                ? createPortal(
                    <SortPanel
                        levels={sortLevels}
                        columns={visibleHeaderCells.map((h) => ({ id: h.id, label: h.label }))}
                        facetsFor={facetsFor}
                        onChange={setSortLevels}
                        onClose={() => setSortPanelOpen(false)}
                    />,
                    document.body
                )
                : null}

            {columnsPanel
                ? createPortal(
                    <ColumnsPanel
                        x={columnsPanel.x}
                        y={columnsPanel.y}
                        columns={headerCells.map((h) => ({ id: h.id, label: h.label }))}
                        hidden={hiddenColumns}
                        lockedIds={LOCKED_COLUMNS}
                        onChange={setHiddenColumns}
                        onClose={() => setColumnsPanel(null)}
                    />,
                    document.body
                )
                : null}

            {/* Dialogs */}
            {dialog.kind === 'schedule' ? (
                <ScheduleStageDialog
                    open
                    onClose={() => setDialog({ kind: 'none' })}
                    jobId={dialog.jobId}
                    jobStageId={dialog.jobStageId}
                    stageName={dialog.stageName}
                    jobLabel={dialog.jobLabel}
                    currentDate={dialog.currentDate}
                    currentCrewId={dialog.currentCrewId}
                />
            ) : null}
            {dialog.kind === 'edit-job' ? (
                <EditJobDialog
                    open
                    onClose={() => setDialog({ kind: 'none' })}
                    job={dialog.job}
                />
            ) : null}
            {dialog.kind === 'flatwork' ? (
                <FlatworkScheduleDialog
                    open
                    onClose={() => setDialog({ kind: 'none' })}
                    jobId={dialog.jobId}
                    jobLabel={dialog.jobLabel}
                    current={dialog.current}
                />
            ) : null}
            {dialog.kind === 'bulk-flatwork' ? (
                <BulkFlatworkDialog
                    open
                    onClose={() => setDialog({ kind: 'none' })}
                    jobs={selectedJobs}
                    flatworkByJob={flatworkByJob}
                />
            ) : null}
        </div>
    );
}
